<template>
	<div class="fight-system-tile-detail">
		<BattlefieldHeader
			:skin="type"
			:supplyLimitValue="ssr.supplyLimitMax - ssr.supplyLimit"
			:supplyLimitMax="ssr.supplyLimitMax"
		/>
		<template v-if="ssr.isUnlocked">
			<slot />
			<FightStatusDetail
				v-if="
					(isEnemy || isFriendly) &&
					!tilerepoFriendlySupplyUnitsFrameNames.has(entity.frameName)
				"
				:currentTileIcon="currentTileIcon"
				:entity="entity"
				:skin="skin"
			/>
			<SiegeTheTarget
				v-if="isEnemy && lockedType === ''"
				:entity="entity"
			/>
			<SingleAttack
				v-if="isEnemy && lockedType === ''"
				:entity="entity"
			/>
		</template>
		<FightLocked v-if="lockedType.length > 0" :lockedType="lockedType" />
	</div>
</template>

<script lang="ts" setup>
import BattlefieldHeader from "@/components/FightSystem/BattlefieldHeader.vue";
import type { LockedType } from "@/components/FightSystem/FightLocked.vue";
import FightLocked from "@/components/FightSystem/FightLocked.vue";
import FightStatusDetail from "@/components/FightSystem/FightStatusDetail.vue";
import SiegeTheTarget from "@/components/FightSystem/SiegeTheTarget.vue";
import SingleAttack from "@/components/FightSystem/SingleAttack.vue";
import {
	tilerepoFightableEnemiesFrameNames,
	tilerepoFriendlySupplyUnitsFrameNames,
	tilerepoFriendlyUnitsFrameNames,
} from "@/game/entities/config";
import type TileEntity from "@/game/entities/TileEntity";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useTileRepository from "@/hooks/useTileRepository";
import router from "@/router";
import { computed, markRaw, onMounted, watch } from "vue";

const { currentTileIcon = "", entity } = defineProps<{
	currentTileIcon: string;
	entity: TileEntity;
}>();
const tilerepo = markRaw(useTileRepository());

const ssr = markRaw(useSiegeSystemRepository());

const isFriendly = computed((): boolean => {
	return tilerepoFriendlyUnitsFrameNames.has(entity.frameName);
});
const isEnemy = computed((): boolean => {
	return tilerepoFightableEnemiesFrameNames.has(entity.frameName);
});

const isChieftain = computed((): boolean => {
	return (
		entity.index ===
		tilerepo.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(entity.index)
	);
});

const type = computed((): "friendly" | "enemy" | "neutral" => {
	if (isFriendly.value) return "friendly";
	if (isEnemy.value) return "enemy";
	return "neutral";
});

const skin = computed((): "friendly" | "enemy" | "enemy-special" => {
	if (isChieftain.value) return "enemy-special";
	if (isEnemy.value) return "enemy";
	return "friendly";
});

const lockedType = computed((): LockedType => {
	if (!ssr.isUnlocked) return "level";

	if (
		!ssr.isWholeEnemyTerritoryRevealedByTileIndex(
			ssr.mainEnemyTileIndex || entity.index,
		)
	)
		return "explore";
	if (!isEnemy.value) return "";
	if (ssr.allFriendlySiegeSystemUnitsTileIndexes.size <= 0) return "deploy";
	if (
		!ssr.canPerformAddSiegeTargetByTileIndex(entity.index) &&
		!ssr.isSiegeTargetByTileIndex(entity.index)
	)
		return "range";
	return "";
});

onMounted((): void => {
	ssr.rerenderBattlefieldLabelByTileIndex(entity.index);
});

// automaticke zavretie tilu, ak unita uz nema hp
watch(
	(): number | null => ssr.getCurrentHpByTileIndex(entity.index),
	(newHp: number | null): void => {
		if (newHp) return;
		router.push("/");
	},
);
</script>

<style lang="scss" scoped>
.fight-system-tile-detail {
	@include flex-col-start;
	gap: 0.5rem;
	padding: 0 0.25rem;
}
</style>
